import { supabase } from "@/integrations/supabase/client";

export interface StandardizedLocation {
  standardized_name: string;
  translations: {
    en: string;
    ar: string;
  };
  lat?: number;
  lng?: number;
  country?: string;
  state?: string;
  iso_code?: string;
}

/**
 * Standardizes a city name and gets its coordinates
 */
export async function standardizeCity(cityName: string): Promise<StandardizedLocation | null> {
  try {
    // First check if we already have this city in our standards table
    const { data: existing, error: queryError } = await supabase
      .from('city_standards')
      .select('*')
      .ilike('standardized_name', cityName)
      .single();

    if (!queryError && existing) {
      return {
        standardized_name: existing.standardized_name,
        translations: existing.translations as { en: string; ar: string },
        lat: existing.lat ? parseFloat(existing.lat.toString()) : undefined,
        lng: existing.lng ? parseFloat(existing.lng.toString()) : undefined,
        country: existing.country,
        state: existing.state
      };
    }

    // If not found, call the standardization function
    const { data, error } = await supabase.functions.invoke('city-standardization', {
      body: { city_name: cityName }
    });

    if (error) {
      console.error('Error standardizing city:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in standardizeCity:', error);
    return null;
  }
}

/**
 * Standardizes a state name and gets its coordinates
 */
export async function standardizeState(stateName: string, country: string = 'Saudi Arabia'): Promise<StandardizedLocation | null> {
  try {
    // First check if we already have this state in our standards table
    const { data: existing, error: queryError } = await supabase
      .from('state_standards')
      .select('*')
      .ilike('standardized_name', stateName)
      .eq('country', country)
      .single();

    if (!queryError && existing) {
      return {
        standardized_name: existing.standardized_name,
        translations: existing.translations as { en: string; ar: string },
        lat: existing.lat ? parseFloat(existing.lat.toString()) : undefined,
        lng: existing.lng ? parseFloat(existing.lng.toString()) : undefined,
        country: existing.country
      };
    }

    // If not found, call the standardization function
    const { data, error } = await supabase.functions.invoke('state-standardization', {
      body: { state_name: stateName, country }
    });

    if (error) {
      console.error('Error standardizing state:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in standardizeState:', error);
    return null;
  }
}

/**
 * Standardizes a country name and gets its details
 */
export async function standardizeCountry(countryName: string): Promise<StandardizedLocation | null> {
  try {
    // First check if we already have this country in our standards table
    const { data: existing, error: queryError } = await supabase
      .from('country_standards')
      .select('*')
      .ilike('standardized_name', countryName)
      .single();

    if (!queryError && existing) {
      return {
        standardized_name: existing.standardized_name,
        translations: existing.translations as { en: string; ar: string },
        iso_code: existing.iso_code
      };
    }

    // If not found, call the standardization function
    const { data, error } = await supabase.functions.invoke('country-standardization', {
      body: { country_name: countryName }
    });

    if (error) {
      console.error('Error standardizing country:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in standardizeCountry:', error);
    return null;
  }
}

/**
 * Process a location (city) and return standardized data with coordinates
 */
export async function processLocation(cityName: string, stateName?: string, countryName: string = 'Saudi Arabia') {
  const [cityData, stateData, countryData] = await Promise.all([
    standardizeCity(cityName),
    stateName ? standardizeState(stateName, countryName) : null,
    standardizeCountry(countryName)
  ]);

  return {
    city: cityData,
    state: stateData,
    country: countryData,
    // Always use the exact standardized names from the standards tables
    standardized_city: cityData?.standardized_name || cityName,
    standardized_state: stateData?.standardized_name || cityData?.state || stateName || 'Unknown',
    standardized_country: countryData?.standardized_name || 'Saudi Arabia',
    // Use city coordinates, fallback to state coordinates if city not found
    lat: cityData?.lat || stateData?.lat || null,
    lng: cityData?.lng || stateData?.lng || null
  };
}